import { Container, Group, Grid, Button, GridCol, Title, Text } from '@mantine/core';
import { useSelector } from 'react-redux';
import { ICurrency } from '../../../../libraries/currencies';
import { Transaction } from '../../../../libraries/transactions';
import { AppState } from '../../../client-redux/reducers';
import ModalComponent from '../../../components/modal';
import DisplayField from '../../../components/display-field';
import { Language } from '../../../languages/languageHandler';
import { formatDateTimeLabel } from '../../../utils/date';
import { formatCurrAmount, getLocationName, formatTransactionType } from '../../../utils/script';
import { processTransactionData } from '../transaction-data';
import TransactionTable from '../transaction-table';
import PeriodsTable from '../cash-collection-table';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

interface ShowTransactionProps {
  transaction: Transaction;
  openDetails: boolean;
  hideBtn: () => void;
  lang: Language;
  currency: ICurrency;
  getLog: () => void;
  getReport: () => void;
  isDeposit?: boolean;
}

const ShowTransaction = ({ transaction, openDetails, hideBtn, currency, getLog, getReport }: ShowTransactionProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const matchingCurrency = currencies?.find(c => c.cid === transaction.cid);

  const processed = processTransactionData(transaction, currencies);
  const receivedBlocks = processed.filter(p => p.received);
  const dispensedBlocks = processed.filter(p => p.dispensed);

  return (
    <>
      <ModalComponent
        show={openDetails}
        handleClick={hideBtn}
        title={`${formatTransactionType(lang, transaction.type)} ${transaction.eid}-${transaction.period}-${transaction.code}`}
        content={
          <>
            <Container classNames={{ root: 'transaction-details' }}>
              <Group className="details">
                <Grid gutter="sm">
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_ID} content={transaction.eid} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_LOCAL} content={getLocationName(transaction.sid)} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLASS_CODE} content={transaction.code} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLOBAL_DATE_TIME} content={formatDateTimeLabel(transaction.dateTime)} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.CASH_COLLECTION_FIRST_PERIOD_OPEN_DATE} content={(transaction.typeData.beginDate && formatDateTimeLabel(transaction.typeData.beginDate)) || '-'} />
                  </GridCol>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.CASH_COLLECTION_LAST_PERIOD_CLOSE_DATE} content={(transaction.typeData.endDate && formatDateTimeLabel(transaction.typeData.endDate)) || '-'} />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.GLOBAL_TOTAL_INVOICED}
                      content={
                        matchingCurrency ? formatCurrAmount(transaction.typeData.invoiced || 0, matchingCurrency) : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.GLOBAL_TOTAL_COLLECTED}
                      content={matchingCurrency ? formatCurrAmount(transaction.value || 0, matchingCurrency) : '-'}
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_EXCESS}
                      content={
                        matchingCurrency ? formatCurrAmount(transaction.typeData.excess || 0, matchingCurrency) : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_CUMULATIVE_EXCESS_PREVIOUS}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(transaction.typeData.prevCumulativeExcess || 0, matchingCurrency)
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_CUMULATIVE_EXCESS}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(transaction.typeData.cumulativeExcess || 0, matchingCurrency)
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_TOTAL_LOADED}
                      content={
                        matchingCurrency
                          ? formatCurrAmount(transaction.typeData.loaded || 0, matchingCurrency)
                          : '-'
                      }
                    />
                  </GridCol>

                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.CASH_COLLECTION_WORKING_CAPITAL_EXCESS_REMOVED}
                      content={transaction.typeData.excessWCRemoved ? '✅' : '-'}
                    />
                  </GridCol>
                </Grid>
              </Group>

              {receivedBlocks.length > 0 && (
                <>
                  <Title className="table-titles">{lang.CURRENCY_TABLE_CASH_IN}</Title>
                  {receivedBlocks.map((block, idx) => (
                    <TransactionTable key={`rec-${idx}`} currency={block.currency} cash={block.denominations} />
                  ))}
                </>
              )}

              <Title className="table-titles">{lang.CASH_COLLECTION_DETAIL}</Title>
              <PeriodsTable
                currency={matchingCurrency}
                typeData={transaction.typeData}
                amount={transaction.value || 0}
                cumulativeExcess={transaction.typeData.cumulativeExcess || 0}
                prevCumulativeExcess={transaction.typeData.prevCumulativeExcess || 0}
              />

              {dispensedBlocks.length > 0 && (
                <>
                  <Title className="table-titles">{lang.CURRENCY_TABLE_CASH_OUT}</Title>
                  {dispensedBlocks.map((block, idx) => (
                    <TransactionTable key={`dis-${idx}`} currency={block.currency} cash={block.denominations} />
                  ))}
                </>
              )}

              <Group className="buttons">
                <Button radius="md" onClick={() => getLog()}>
                  <Icon.IconDownload stroke={2} />
                  Logs
                </Button>
                <Button radius="md" onClick={() => getReport()}>
                  <Icon.IconFileTypePdf stroke={2} />
                  Report
                </Button>
              </Group>

              {transaction.softVersion && (
                <Group className="footer-version">
                  <Text>{`${lang.EQUIPMENT_SOFTWARE_VERSION}: ${transaction.softVersion}`}</Text>
                </Group>
              )}
            </Container>
          </>
        }
      />
    </>
  );
};

export default ShowTransaction;
